import {
  IPortfolio,
  PORTFOLIO_KEY_FIELD,
  PORTFOLIO_SEARCH_FIELDS,
  PortfolioField,
} from "@/lib/interfaces and Enums/ENUMPortfolio";
import {
  DEFAULT_SEARCH_LIMIT,
  ISearchResult,
  findItemByFieldValue,
  searchItemsByFields,
} from "@/lib/referenceDataSearchHelper";
import portfoliosStore from "./portfoliosStore";
import { loadReferenceDataOnce } from "./referenceDataLoadHelper";

// LQC-2254 - GET /Portfolios, called on demand when the Leg Creation widget
// mounts. NOT part of the app-load path: a user who never adds the widget never
// triggers it. See widgets/tradeFromScratch/README.md.

export const PORTFOLIOS_ENDPOINT = "/Portfolios";

// ----- Pure transform -------------------------------------------------------

// Unwrap { portfolios: [...] }. Returns null for anything else so the loader can
// flag a contract mismatch rather than seeding an empty list.
export function extractPortfolios(response: any): IPortfolio[] | null {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.portfolios)) return response.portfolios;
  return null;
}

// ----- Feed -----------------------------------------------------------------

export async function getAllPortfolios(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  const store = portfoliosStore.getState();
  await loadReferenceDataOnce<IPortfolio>({
    url: PORTFOLIOS_ENDPOINT,
    source: "portfoliosStoreACTIONS.getAllPortfolios",
    getAccessTokenSilently,
    readStatus: () => portfoliosStore.getState().portfoliosStatus,
    setStatus: store.setPortfoliosStatus,
    setItems: store.setPortfolios,
    setError: store.setPortfoliosError,
    extractItems: extractPortfolios,
    forceReload,
  });
}

// ----- Search / lookup ------------------------------------------------------

// Type-to-find over code, name and customer. An empty term returns the head of the
// list so a dropdown has something to show before the user types. The result carries
// totalMatches so the caller can show how much was cut off by the limit.
export function searchPortfolios(
  term: string,
  limit: number = DEFAULT_SEARCH_LIMIT,
): ISearchResult<IPortfolio> {
  return searchItemsByFields<IPortfolio>({
    items: portfoliosStore.getState().portfolios,
    term,
    fields: PORTFOLIO_SEARCH_FIELDS,
    limit,
  });
}

// Confirms a chosen portfolio really exists in our data before it is sent.
export function findPortfolioByCode(
  portfolioCode: string,
): IPortfolio | undefined {
  return findItemByFieldValue<IPortfolio>(
    portfoliosStore.getState().portfolios,
    PORTFOLIO_KEY_FIELD,
    portfolioCode,
  );
}

export function findPortfolioByName(
  portfolioName: string,
): IPortfolio | undefined {
  return findItemByFieldValue<IPortfolio>(
    portfoliosStore.getState().portfolios,
    PortfolioField.portfolioName,
    portfolioName,
  );
}

// Validation gate for the send path - a portfolio code we cannot resolve must not
// reach the back end.
export function isKnownPortfolioCode(portfolioCode: string): boolean {
  return findPortfolioByCode(portfolioCode) !== undefined;
}
